import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { X, Building2, DollarSign, MapPin, CheckCircle2 } from 'lucide-react';

const ProjectModal = ({ project, isOpen, onClose }) => {
  if (!project) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-navy-card border-navy-border text-white max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex justify-between items-start mb-6">
            <div className="flex-1">
              <div className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-semibold">
                {project.sector}
              </div>
              <DialogTitle className="text-4xl font-bold text-white mb-2">
                {project.title}
              </DialogTitle>
              {project.subtitle && (
                <p className="text-lg text-gray-400">{project.subtitle}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors ml-4"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </DialogHeader>
        
        <div className="space-y-8">
          {/* Key Metrics */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white bg-opacity-5 p-6 rounded-sm">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-5 h-5 text-gray-400" />
                <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Project Value</span>
              </div>
              <div className="text-3xl font-bold">{project.value}</div>
            </div>
            {project.location && (
              <div className="bg-white bg-opacity-5 p-6 rounded-sm">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-5 h-5 text-gray-400" />
                  <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Location</span>
                </div>
                <div className="text-lg font-semibold">{project.location}</div>
              </div>
            )}
          </div>

          {/* Overview */}
          <div>
            <h3 className="text-2xl font-bold mb-4 text-white">Project Overview</h3>
            <DialogDescription className="text-gray-300 leading-relaxed text-lg">
              {project.description}
            </DialogDescription>
          </div>

          {/* Role */}
          {project.role && (
            <div className="bg-white bg-opacity-5 p-6 rounded-sm">
              <h4 className="text-sm text-gray-500 uppercase tracking-wider font-semibold mb-2">
                Role & Responsibilities
              </h4>
              <p className="text-lg text-white font-semibold">{project.role}</p>
            </div>
          )}

          {/* Scope Highlights */}
          {project.scope && project.scope.length > 0 && (
            <div>
              <h3 className="text-2xl font-bold mb-4 text-white">Scope Highlights</h3>
              <ul className="space-y-3">
                {project.scope.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-white mt-1 flex-shrink-0" strokeWidth={2} />
                    <span className="text-gray-300 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Outcomes */}
          {project.outcomes && (
            <div className="bg-white bg-opacity-5 p-8 rounded-sm">
              <h3 className="text-2xl font-bold mb-4 text-white">Key Outcomes</h3>
              <p className="text-gray-300 leading-relaxed text-lg">
                {project.outcomes}
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectModal;
